import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  CalendarCheck, ShoppingBag, Target, Wallet, AlertCircle,
  CheckCircle2, ArrowRight, Plus, TrendingUp, Sparkles,
  Users as UsersIcon, ClipboardCheck, CheckSquare, Boxes, FileText,
  ShieldCheck, BarChart3, UserX, Package, Store,
} from "lucide-react";

// Phase 6.1 hardening — accept canonical roles AND defensive variants
// ("Sales Promoter", "sales-promoter", " self_promoter ", etc.). Only the
// promoter personas trigger the self-profile dashboard; every other role
// (admin, product_manager, ccr, sales_head, service_head, sales_manager,
//  manager, accounts_finance, technician, ...) gets the Operations Dashboard.
const PROMOTER_ROLE_KEYS = new Set([
  "sales_promoter", "self_promoter", "promoter",
]);

function normalizeRole(role) {
  if (!role) return "";
  return String(role).trim().toLowerCase().replace(/[\s-]+/g, "_");
}

function isPromoterRole(role) {
  return PROMOTER_ROLE_KEYS.has(normalizeRole(role));
}

/**
 * Promoter Portal landing page.
 *
 * - sales_promoter / self_promoter → Mobile dashboard with own profile + KPIs.
 * - admin / product_manager / sales_head / sales_manager / ccr / accounts_finance
 *   → Operations command-center with quick links to Phase 6.1 surfaces.
 *   These roles NEVER hit /promoter/me and NEVER see "Could not load your profile".
 */
export default function PromoterDashboard() {
  const { user, loading } = useAuth();
  // While AuthContext is still resolving, render an empty Operations shell so
  // the user never sees the promoter self error flash.
  if (loading) {
    return <div className="p-6 text-sm text-slate-500" data-testid="ops-dashboard-loading">Loading…</div>;
  }
  if (isPromoterRole(user?.role)) return <PromoterSelfDashboard />;
  return <OperationsDashboard user={user} />;
}

function PromoterSelfDashboard() {
  const [profile, setProfile] = useState(null);
  const [today, setToday] = useState(null);
  const [calc, setCalc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const p = await api.get("/promoter/me");
        if (!mounted) return;
        setProfile(p.data);
        const [t, c] = await Promise.all([
          api.get("/promoter/attendance/today").catch(() => ({ data: null })),
          api.get("/promoter/calc/me").catch((e) => { console.warn(e); return { data: null }; }),
        ]);
        if (!mounted) return;
        setToday(t.data && t.data.id ? t.data : null);
        setCalc(c.data);
      } catch (e) {
        if (mounted) setError(e?.response?.data?.detail || "Could not load profile");
      }
    })();
    return () => { mounted = false; };
  }, []);

  const isSelfPromoter = profile?.persona === "self_promoter";
  const attendanceLabel = !today ? "Not checked in"
    : today.check_out_at ? "Day complete"
    : "On field";

  return (
    <div className="space-y-4" data-testid="promoter-dashboard">
      <header>
        <h1 className="font-heading text-2xl font-bold text-slate-900">
          {profile ? `Hi, ${profile.name?.split(" ")[0] || "Promoter"}` : "Welcome"}
        </h1>
        <p className="text-xs text-slate-500 mt-0.5">
          {profile?.promoter_id ? `ID ${profile.promoter_id}` : "Loading…"}
          {profile?.assigned_dealer_name ? ` · ${profile.assigned_dealer_name}` : ""}
          {isSelfPromoter ? " · Self Promoter" : ""}
        </p>
      </header>

      {error && (
        <Card data-testid="promoter-dashboard-error">
          <CardContent className="p-4 flex items-start gap-2 text-rose-700">
            <AlertCircle size={18} className="mt-0.5" />
            <div>
              <div className="font-semibold text-sm">Could not load your profile</div>
              <div className="text-xs">{error}</div>
              <div className="text-xs text-slate-500 mt-1">
                If you just received your login, ask your Product Manager to verify it&apos;s linked to your promoter record.
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {profile && (
        <Card data-testid="promoter-quick-actions">
          <CardContent className="p-3 grid grid-cols-2 gap-2">
            {!isSelfPromoter && (
              <Link to="/promoter/attendance" className="block">
                <Button variant="outline" className="w-full justify-between" data-testid="qa-attendance-btn">
                  <span className="flex items-center gap-2">
                    {today ? <CheckCircle2 size={14} className="text-emerald-600" /> : <CalendarCheck size={14} />}
                    {attendanceLabel}
                  </span>
                  <ArrowRight size={12} />
                </Button>
              </Link>
            )}
            <Link to="/promoter/sales/new" className="block">
              <Button className="w-full justify-between bg-slate-900 hover:bg-slate-800 text-white"
                      data-testid="qa-new-sale-btn">
                <span className="flex items-center gap-2"><Plus size={14} /> New sale</span>
                <ArrowRight size={12} />
              </Button>
            </Link>
            <Link to="/promoter/reports" className="block">
              <Button variant="outline" className="w-full justify-between"
                      data-testid="qa-my-reports-btn">
                <span className="flex items-center gap-2"><TrendingUp size={14} /> My reports</span>
                <ArrowRight size={12} />
              </Button>
            </Link>
            {isSelfPromoter && (
              <Link to="/promoter/my-schemes" className="block">
                <Button variant="outline" className="w-full justify-between"
                        data-testid="qa-my-schemes-btn">
                  <span className="flex items-center gap-2"><Sparkles size={14} className="text-amber-500" /> My schemes</span>
                  <ArrowRight size={12} />
                </Button>
              </Link>
            )}
          </CardContent>
        </Card>
      )}

      <PromoterSelfKpis calc={calc} profile={profile} />
    </div>
  );
}

function PromoterSelfKpis({ calc, profile }) {
  if (!calc || !profile) return null;
  const isSelfPromoter = profile?.persona === "self_promoter";
  const targetAmount = calc?.target?.sales_amount || 0;
  const achievement = calc?.achievement_pct || 0;
  const progressPct = Math.min(Math.max(achievement, 0), 100);
  return (
    <>
      {!isSelfPromoter && (
        <Card data-testid="promoter-target-card">
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center gap-2 text-slate-600 text-sm">
              <Target size={14} /> Target progress
            </div>
            <div className="text-2xl font-bold text-slate-900">{achievement.toFixed(1)}%</div>
            <div className="w-full bg-slate-100 h-2 rounded-full overflow-hidden">
              <div className="bg-emerald-500 h-2" style={{ width: `${progressPct}%` }} />
            </div>
            {targetAmount > 0 && (
              <div className="text-xs text-slate-500">
                Rs. {Number(calc?.approved_sales_value || 0).toLocaleString()} of
                Rs. {Number(targetAmount).toLocaleString()} (Net)
              </div>
            )}
          </CardContent>
        </Card>
      )}
      <Card data-testid="promoter-earnings-card">
        <CardContent className="p-4 space-y-1">
          <div className="flex items-center gap-2 text-slate-600 text-sm"><Wallet size={14} /> Payable incentive</div>
          <div className="text-2xl font-bold text-slate-900">Rs. {Number(calc?.payable_incentive || 0).toLocaleString()}</div>
          <div className="text-xs text-slate-500">
            Approved sales: {calc?.approved_count || 0} · Pending: {calc?.pending_count || 0}
          </div>
        </CardContent>
      </Card>
      {Number(calc?.no_rule_value || 0) > 0 && (
        <Card data-testid="no-rule-warning">
          <CardContent className="p-3 text-xs flex gap-2 items-start text-amber-800 bg-amber-50/60">
            <AlertCircle size={14} className="mt-0.5" />
            <div>
              Rs. {Number(calc.no_rule_value).toLocaleString()} of your approved sales had no matching
              incentive rule. Ask your Product Manager to confirm.
            </div>
          </CardContent>
        </Card>
      )}
    </>
  );
}

/* ---------------- Operations / Command-center dashboard ---------------- */
function OperationsDashboard({ user }) {
  const { can } = useAuth();
  const role = user?.role;
  const greet = `Hi, ${(user?.name || "Team").split(" ")[0]}`;
  const tiles = [
    { to: "/promoter/manage", label: "Promoter Management", desc: "Onboard, edit, deactivate promoters.", icon: UsersIcon, perm: "promoters.view" },
    { to: "/promoter/attendance-mgmt", label: "Attendance", desc: "Daily attendance + leave decisions.", icon: ClipboardCheck, perm: "promoter_attendance.view" },
    { to: "/promoter/sales-mgmt", label: "Promoter Sales", desc: "Browse, filter and audit sales submissions.", icon: ShoppingBag, perm: "promoter_sales.view" },
    { to: "/promoter/sales-approval", label: "Sales Approval", desc: "Approve / reject pending sales.", icon: CheckSquare, perm: "promoter_sales.approve" },
    { to: "/promoter/stock-mgmt", label: "Stock & Display", desc: "Dealer stock + display reports.", icon: Boxes, perm: "promoter_stock.view" },
    { to: "/promoter/financial-rules", label: "Financial Rules", desc: "Propose rules / targets / slabs.", icon: FileText, perm: "promoter_financial.propose" },
    { to: "/promoter/bulk-incentive", label: "Bulk Incentive Setup", desc: "Excel-driven incentive proposals + admin approvals.", icon: FileText, perm: "promoter_financial.propose" },
    { to: "/promoter/financial-approvals", label: "Financial Approvals", desc: "Approve maker-checker proposals.", icon: ShieldCheck, perm: "promoter_financial.approve" },
    { to: "/promoter/incentive-summary", label: "Incentive Summary", desc: "BS / AD calendar-aware incentive report.", icon: BarChart3, perm: "promoter_sales.view" },
    { to: "/promoter/schemes", label: "Sell-Out Schemes", desc: "Dealer schemes maker-checker + ledger.", icon: Sparkles, perm: "promoter_financial.view" },
    { to: "/promoter/terminations", label: "Terminations", desc: "Initiate and clear off-boarding.", icon: UserX, perm: "promoter_termination.view" },
  ];
  // Master-data shortcuts (PM/Admin/CCR). CCR sees only Dealers per Phase 6.1.A.
  const masterTiles = [];
  if (can("products.add")) {
    masterTiles.push({ to: "/products", label: "Products", desc: "Direct create + browse master products.", icon: Package, external: true });
  }
  if (can("dealers.add")) {
    masterTiles.push({ to: "/dealers", label: "Dealers", desc: "Direct create + browse dealer counters.", icon: Store, external: true });
  }

  const visible = tiles.filter((t) => !t.perm || can(t.perm));

  return (
    <div className="space-y-6" data-testid="ops-dashboard">
      <header>
        <h1 className="font-heading text-3xl font-bold text-slate-900">{greet}</h1>
        <p className="text-sm text-slate-500 mt-1">
          Promoter portal operations console
          {role ? <> · <span className="capitalize">{role.replace(/_/g, " ")}</span></> : null}
        </p>
      </header>

      <Card data-testid="ops-tiles">
        <CardContent className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {visible.length === 0 && (
            <div className="text-sm text-slate-500 col-span-full p-3">
              You don&apos;t have access to any promoter portal operations. Contact an admin to extend your permissions.
            </div>
          )}
          {visible.map((t) => (
            <TileLink key={t.to} {...t} />
          ))}
        </CardContent>
      </Card>

      {masterTiles.length > 0 && (
        <div data-testid="ops-master-shortcuts">
          <h2 className="text-sm font-semibold text-slate-700 mb-2">Master data shortcuts</h2>
          <Card>
            <CardContent className="p-3 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {masterTiles.map((t) => <TileLink key={t.to} {...t} />)}
            </CardContent>
          </Card>
          <p className="text-[11px] text-slate-500 mt-1">
            Master records (products / dealers) live in the Service CRM. Phase 6.1.A grants direct create rights to Product Manager (products + dealers) and to CCR (dealers only).
          </p>
        </div>
      )}
    </div>
  );
}

function TileLink({ to, label, desc, icon: Icon, external }) {
  const inner = (
    <Card className="hover:border-slate-400 transition-colors cursor-pointer h-full" data-testid={`ops-tile-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <CardContent className="p-3 flex items-start gap-3">
        <div className="rounded-md bg-slate-900 text-white p-2"><Icon size={18} /></div>
        <div className="flex-1">
          <div className="font-semibold text-slate-900 text-sm">{label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{desc}</div>
        </div>
        <ArrowRight size={14} className="text-slate-400 mt-1" />
      </CardContent>
    </Card>
  );
  if (external) return <a href={to}>{inner}</a>;
  return <Link to={to}>{inner}</Link>;
}

function KpiTile({ icon: Icon, label, value, hint, testId }) {
  return (
    <Card data-testid={testId}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 text-slate-500 text-[11px] uppercase tracking-wide">
          <Icon size={14} />
          <span>{label}</span>
        </div>
        <div className="font-heading text-xl font-bold text-slate-900 mt-1 truncate">{value}</div>
        {hint && <div className="text-[10px] text-slate-400 mt-1 truncate">{hint}</div>}
      </CardContent>
    </Card>
  );
}
